import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { MaskedLine, FadeIn, SectionLabel } from "@/components/Reveal";
import { SERVICES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUDGETS = ["Under ₹50k", "₹50k – ₹2L", "₹2L – ₹10L", "₹10L+"];

const inputCls =
  "w-full rounded-none border border-bm-line bg-white px-4 py-3.5 font-manrope text-sm text-bm-ink outline-none transition-colors duration-300 focus:border-bm-blue placeholder:text-bm-gray/50";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent! Our team will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", service: "", budget: "", message: "" });
    } catch (err) {
      toast.error("Could not send your enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-white">
      <section className="pt-40 pb-24 lg:pb-32 grain relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-6">
            <FadeIn>
              <SectionLabel>Contact</SectionLabel>
            </FadeIn>
            <h1 className="mt-6 font-syne font-extrabold tracking-tighter leading-[0.95] text-[13vw] sm:text-7xl text-bm-ink">
              <MaskedLine delay={0.1}>LET'S BUILD</MaskedLine>
              <MaskedLine delay={0.24}>
                <span className="text-bm-blue">YOUR BRAND,</span>
              </MaskedLine>
              <MaskedLine delay={0.38}>
                <span className="text-stroke-blue">AROUND THE CLOCK.</span>
              </MaskedLine>
            </h1>
            <FadeIn delay={0.5}>
              <p className="mt-8 max-w-md font-manrope text-base leading-relaxed text-bm-gray">
                Tell us where you are and where you want to be. We'll reply
                with a plan — usually before your next coffee.
              </p>
              <ul className="mt-10 space-y-5">
                {[
                  [Mail, "hello@brandmitra24.com", "contact-email"],
                  [Phone, "+91 97691 08757", "contact-phone"],
                  [MapPin, "Abhijit Apartment, Old Prabhadevi Road, Mumbai 400 025", "contact-location"],
                ].map(([Icon, text, tid]) => (
                  <li key={tid} data-testid={tid} className="flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-bm-blue/10 text-bm-blue">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="font-manrope text-sm font-semibold text-bm-ink">{text}</span>
                  </li>
                ))}
              </ul>
            </FadeIn>

            <FadeIn delay={0.55} className="mt-10">
              <div
                data-testid="contact-map"
                className="overflow-hidden rounded-xl border border-bm-line"
              >
                <iframe
                  title="Brand Mitra 24 — Abhijit Apartment, Old Prabhadevi Road, Mumbai"
                  src="https://www.google.com/maps?q=Abhijit+Apartment,+Old+Prabhadevi+Road,+Mumbai+400025&output=embed"
                  className="h-72 w-full sm:h-96"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.35} className="lg:col-span-6">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="border border-bm-line bg-bm-surface p-8 sm:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="mb-2 block font-manrope text-xs font-bold uppercase tracking-widest text-bm-ink">Name *</label>
                  <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block font-manrope text-xs font-bold uppercase tracking-widest text-bm-ink">Email *</label>
                  <input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block font-manrope text-xs font-bold uppercase tracking-widest text-bm-ink">Phone</label>
                  <input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block font-manrope text-xs font-bold uppercase tracking-widest text-bm-ink">Service</label>
                  <select data-testid="contact-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                    <option value="">Select a service</option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.title}>{s.title}</option>
                    ))}
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block font-manrope text-xs font-bold uppercase tracking-widest text-bm-ink">Budget</label>
                  <div className="flex flex-wrap gap-2" data-testid="contact-budget-options">
                    {BUDGETS.map((b) => (
                      <button
                        type="button"
                        key={b}
                        data-testid={`budget-${b.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        onClick={() => setForm({ ...form, budget: form.budget === b ? "" : b })}
                        className={`rounded-full border px-4 py-2 font-manrope text-xs font-bold transition-colors duration-300 ${
                          form.budget === b
                            ? "border-bm-blue bg-bm-blue text-white"
                            : "border-bm-line bg-white text-bm-ink hover:border-bm-blue"
                        }`}
                      >
                        {b}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block font-manrope text-xs font-bold uppercase tracking-widest text-bm-ink">Project details *</label>
                  <textarea data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about your brand, goals and timeline..." className={`${inputCls} resize-none`} />
                </div>
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={loading}
                className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-bm-blue px-8 py-4 font-manrope text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-bm-navy disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Send Enquiry
                    <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
